/**
 * usePaymentAnalytics Hook
 *
 * Fetches payment analytics data using URQL query.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const PaymentAnalyticsDocument = `
  query PaymentAnalytics($filter: PaymentAnalyticsFilter) {
    paymentAnalytics(filter: $filter) {
      totalRevenue
      totalOrders
      averageOrderValue
      primaryCurrency
      byStatus {
        status
        count
        amount
        currency
      }
      byProvider {
        provider
        count
        amount
        currency
        successRate
      }
      byCurrency {
        currency
        count
        amount
        percentage
      }
      pendingCount
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface PaymentStatusStats {
  status: string;
  count: number;
  amount: string;
  currency: string;
}

interface PaymentProviderStats {
  provider: string;
  count: number;
  amount: string;
  currency: string;
  successRate: number;
}

interface PaymentCurrencyStats {
  currency: string;
  count: number;
  amount: string;
  percentage: number;
}

export interface PaymentAnalytics {
  totalRevenue: string;
  totalOrders: number;
  averageOrderValue: string;
  primaryCurrency: string;
  byStatus: PaymentStatusStats[];
  byProvider: PaymentProviderStats[];
  byCurrency: PaymentCurrencyStats[];
  pendingCount: number;
}

interface PaymentAnalyticsFilter {
  dateFrom?: string;
  dateTo?: string;
}

interface UsePaymentAnalyticsOptions {
  filter?: PaymentAnalyticsFilter;
  pause?: boolean;
}

interface UsePaymentAnalyticsResult {
  analytics: PaymentAnalytics | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch payment analytics data.
 */
export function usePaymentAnalytics(
  options: UsePaymentAnalyticsOptions = {}
): UsePaymentAnalyticsResult {
  const { filter, pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    paymentAnalytics: PaymentAnalytics;
  }>({
    query: PaymentAnalyticsDocument,
    variables: { filter },
    pause,
  });

  return {
    analytics: result.data?.paymentAnalytics ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
